import React, { useCallback, useMemo, useRef } from 'react';
import { useAtomValue } from 'jotai';
import { Loadable } from 'jotai/vanilla/utils/loadable';
import { BaseCard } from 'components/base-card';
import {
  AcreageUnitInterestFormula,
  LateralUnitInterestFormula,
  UnitInterestCardContent,
} from 'components/unit-interest-card';
import { TractNRIFormulaData } from 'components/tract-nri-card';
import { formulaDetailsAtomFamily } from 'atoms/formula-details-atoms';
import type { FormulaDetailsResponse } from 'types/api-responses';

interface LazyUnitInterestCardProps {
  nriCalculationId: number;
  unitInterestValue: number | null;
  calculationType: 'NRI' | 'WI';
  renderTrigger: (active: boolean, toggle: () => void) => React.ReactNode;
  /**
   * Optional editor rendered under the formula, given the basis details from the
   * loaded payload. Only the CRM wizard supplies one; every other surface stays
   * read-only.
   */
  renderAcreageBasisPanel?: (
    nriFormula: NonNullable<FormulaDetailsResponse['nriFormula']>,
    close: () => void
  ) => React.ReactNode;
}

// Builds formula data from FormulaDetailsResponse into the format UnitInterestCard expects
export const buildUnitInterestFormula = (
  nriFormula: FormulaDetailsResponse['nriFormula']
): {
  allocationMethod: 'acreage' | 'lateral_length';
  acreageFormula: AcreageUnitInterestFormula | null;
  lateralFormula: LateralUnitInterestFormula | null;
} => {
  if (!nriFormula) {
    return {
      allocationMethod: 'acreage',
      acreageFormula: null,
      lateralFormula: null,
    };
  }

  const isLateral = nriFormula.measureUnit === 'ft';
  const wi = nriFormula.tractWi;
  const hasAllocationManager = nriFormula.allocationManagerPresent;

  // When no allocation manager, use unitGisAc for uatAc, unitTractGrossAcresAc, and uatGisAc
  const unitGisAc = nriFormula.unitGisAc ?? 0;

  if (isLateral) {
    const lateralFormula: LateralUnitInterestFormula = {
      tractNri: nriFormula.tractNri ?? 0,
      wi: wi ?? 0,
      uatFt: nriFormula.uatMeasureValue ?? 0,
      totalLlFt: nriFormula.unitMeasureValue ?? 0,
      participationTractAc: String(nriFormula.participationTractGisAc ?? 0),
      uatGisAc: hasAllocationManager ? (nriFormula.uatGisAc ?? 0) : unitGisAc,
    };
    return {
      allocationMethod: 'lateral_length',
      acreageFormula: null,
      lateralFormula,
    };
  } else {
    const acreageFormula: AcreageUnitInterestFormula = {
      tractNri: nriFormula.tractNri ?? 0,
      wi: wi ?? 0,
      uatAc: hasAllocationManager
        ? (nriFormula.uatMeasureValue ?? 0)
        : unitGisAc,
      uatGisAc: hasAllocationManager ? (nriFormula.uatGisAc ?? 0) : unitGisAc,
      unitTractGrossAcresAc: hasAllocationManager
        ? (nriFormula.unitMeasureValue ?? 0)
        : unitGisAc,
      participationTractAc: String(nriFormula.participationTractGisAc ?? 0),
    };
    return {
      allocationMethod: 'acreage',
      acreageFormula,
      lateralFormula: null,
    };
  }
};

// Converts FormulaDetailsTractFormula to TractNRIFormulaData. Internal —
// only LazyUnitInterestCard consumes it (the tract-fraction breakdown reuses
// deriveCalculationMethod instead).
const buildTractNriFormula = (
  tractFormula: FormulaDetailsResponse['tractNriFormula']
): TractNRIFormulaData | null => {
  if (!tractFormula) return null;

  return {
    netRoyaltyAcres: tractFormula.nra ?? undefined,
    netMineralAcres: tractFormula.nma ?? undefined,
    royaltyRate: tractFormula.leaseRoyaltyRate,
    totalTractAcres: tractFormula.grossAcres,
    instrumentType: tractFormula.instrumentType,
    orriBurdens: tractFormula.orriBurdens,
  };
};

// Derives calculation method from tract formula
export const deriveCalculationMethod = (
  tractFormula: FormulaDetailsResponse['tractNriFormula']
): 'nra' | 'nma' => {
  if (!tractFormula?.netAcreageUnits) return 'nra';
  return tractFormula.netAcreageUnits.toUpperCase() === 'NMA' ? 'nma' : 'nra';
};

/**
 * LazyUnitInterestCard - Fetches formula data on demand when trigger is clicked.
 * Shows loading state while fetching, then renders UnitInterestCard with the data.
 * @return {JSX.Element} The lazy unit interest card component
 */
export const LazyUnitInterestCard: React.FC<LazyUnitInterestCardProps> = ({
  nriCalculationId,
  unitInterestValue,
  calculationType,
  renderTrigger,
  renderAcreageBasisPanel,
}) => {
  // Use atomFamily to get loadable atom for this specific nriCalculationId
  const formulaLoadable = useAtomValue(
    formulaDetailsAtomFamily(nriCalculationId)
  ) as Loadable<FormulaDetailsResponse>;

  // BaseCard owns the open state and only hands the toggle to the trigger, so
  // it is captured here to give the content a way to close itself. Preferred
  // over widening BaseCard's `content` contract, which five other surfaces use.
  const toggleRef = useRef<(() => void) | null>(null);
  const wrappedRenderTrigger = useCallback(
    (active: boolean, toggle: () => void) => {
      toggleRef.current = toggle;
      return renderTrigger(active, toggle);
    },
    [renderTrigger]
  );
  const close = useCallback(() => toggleRef.current?.(), []);

  const content = useMemo(() => {
    if (formulaLoadable.state === 'loading') {
      return (
        <div>
          <span>Loading formula...</span>
        </div>
      );
    }

    if (formulaLoadable.state === 'hasError') {
      return (
        <div>
          <span>Failed to load formula</span>
        </div>
      );
    }

    const formulaDetails = formulaLoadable.data;
    const { allocationMethod, acreageFormula, lateralFormula } =
      buildUnitInterestFormula(formulaDetails.nriFormula);
    const tractNriFormula = buildTractNriFormula(
      calculationType === 'NRI'
        ? formulaDetails.tractNriFormula
        : formulaDetails.tractWiFormula
    );
    const calculationMethod = deriveCalculationMethod(
      formulaDetails.tractNriFormula
    );

    return (
      <UnitInterestCardContent
        unitInterestValue={unitInterestValue}
        activeAllocationMethod={allocationMethod}
        lateralUnitInterestFormula={lateralFormula}
        acreageUnitInterestFormula={acreageFormula}
        hasAllocationManager={
          formulaDetails.nriFormula?.allocationManagerPresent ?? false
        }
        calculationMethod={calculationMethod}
        tractNRIFormula={tractNriFormula}
        title={`UAT ${calculationType} Calculation`}
        acreageBasis={formulaDetails.nriFormula?.acreageBasis ?? 'gis'}
        acreageBasisPanel={
          formulaDetails.nriFormula && renderAcreageBasisPanel
            ? renderAcreageBasisPanel(formulaDetails.nriFormula, close)
            : undefined
        }
      />
    );
  }, [
    formulaLoadable,
    unitInterestValue,
    calculationType,
    renderAcreageBasisPanel,
    close,
  ]);

  return <BaseCard renderTrigger={wrappedRenderTrigger} content={content} />;
};

export default LazyUnitInterestCard;
